import React from 'react';
import type { ColumnsType, ColumnType } from 'antd/es/table';
import { TableNew } from '@dynamika-shared/ui/TableNew/TableNew';
import ChevronIcon from '@dynamika-shared/ui/icons/Chevron';
import { createReadingTasksTableConfig } from './ReadingTasksTableConfig';
import type { Props, ReadingTaskDetailRow, ReadingTaskRow } from './types';
import { getReadingCodeText } from './ReadingTasks.helpers';
import { JsonPayloadModal } from '../common/JsonPayloadModal';
import {
  SupportReadingTask,
  SupportTaskDefinition,
} from '@dynamika-shared/types/subjects';

type ReadingTasksTableConfig = ReturnType<typeof createReadingTasksTableConfig>;
type ToggleGroup = (groupKey: string) => void;

const getTextValue = (value?: string | null): string | undefined => {
  const trimmed = value?.trim();
  return trimmed || undefined;
};

const normalizeVisitId = (visitId?: string | null): string | null =>
  visitId ?? null;

const buildReadingTaskGroupKey = (
  visitId?: string | null,
  taskDefinitionId?: string | null,
  taskDefinitionName?: string | null,
  taskId?: string | null
): string => {
  const normalizedVisit = normalizeVisitId(visitId);
  const identity =
    taskDefinitionId?.trim() ||
    taskDefinitionName?.trim() ||
    taskId?.trim() ||
    'reading-task';

  return JSON.stringify([normalizedVisit, identity]);
};

const getTaskDisplayName = (readingTask: SupportReadingTask): string =>
  readingTask.inventoryName ??
  readingTask.tagName ??
  readingTask.taskDefinitionName ??
  'Reading task';

const getTaskSummaryName = (
  definition?: SupportTaskDefinition,
  task?: SupportReadingTask
): string =>
  definition?.taskDefinitionName ??
  task?.taskDefinitionName ??
  task?.inventoryName ??
  task?.tagName ??
  'Reading task';

const getTaskSummaryUser = (
  definition?: SupportTaskDefinition,
  task?: SupportReadingTask
): string =>
  getTextValue(definition?.taskDefinitionExecutionHistoryUserEmail) ??
  getTextValue(task?.taskDefinitionExecutionHistoryUserEmail) ??
  getTextValue(task?.taskAssigneeEmail) ??
  'Automatic';

const getTaskSummaryStartDate = (
  definition?: SupportTaskDefinition,
  task?: SupportReadingTask
): string =>
  definition?.taskDefinitionExecutionHistoryDate ??
  task?.taskDefinitionExecutionHistoryDate ??
  task?.startDate ??
  '';

const getTaskSummaryEndDate = (
  definition?: SupportTaskDefinition,
  task?: SupportReadingTask
): string =>
  definition?.taskDefinitionExecutionHistoryDate ??
  task?.taskDefinitionExecutionHistoryDate ??
  task?.completionDate ??
  task?.startDate ??
  '';

const getTaskSummaryStatus = (
  definition?: SupportTaskDefinition,
  task?: SupportReadingTask
): string =>
  definition?.taskDefinitionExecutionHistoryStatus ??
  task?.status ??
  '';

type ReadingTaskBucket = {
  definition?: SupportTaskDefinition;
  tasks: SupportReadingTask[];
  sortOrder: number;
};

const getBucketKey = (
  visitId?: string | null,
  taskDefinitionId?: string | null,
  taskDefinitionName?: string | null,
  taskId?: string | null
): string =>
  buildReadingTaskGroupKey(
    visitId,
    taskDefinitionId,
    taskDefinitionName,
    taskId
  );

const createReadingTaskDetailRow = (
  readingTask: SupportReadingTask,
  groupKey: string,
  detailId: string
): ReadingTaskRow => ({
  type: 'detail',
  key: `${groupKey}:${detailId}`,
  groupKey,
  taskDefinition: getTaskDisplayName(readingTask),
  user: getTextValue(readingTask.taskAssigneeEmail) ?? 'Automatic',
  userSecondaryText: readingTask.readerRoleName ?? '',
  startDate: readingTask.startDate ?? '',
  endDate: readingTask.completionDate ?? '',
  status: readingTask.status ?? '',
  resultData: readingTask.result,
  taskType: readingTask.taskType ?? '',
  readingCodeText: getReadingCodeText(readingTask),
  deleted: readingTask.deleted === true,
});

const buildReadingTaskBuckets = (
  definitionsForScope: SupportTaskDefinition[],
  tasksForScope: SupportReadingTask[]
): Map<string, ReadingTaskBucket> => {
  const buckets = new Map<string, ReadingTaskBucket>();

  for (const [definitionIndex, definition] of definitionsForScope.entries()) {
    const bucketKey = getBucketKey(
      definition.visitId,
      definition.taskDefinitionId,
      definition.taskDefinitionName
    );
    const existingBucket = buckets.get(bucketKey);

    if (existingBucket) {
      existingBucket.definition ??= definition;
    } else {
      buckets.set(bucketKey, {
        definition,
        tasks: [],
        sortOrder: definitionIndex,
      });
    }
  }

  const fallbackSortBase = definitionsForScope.length;

  for (const task of tasksForScope) {
    const bucketKey = getBucketKey(
      task.visitId,
      task.taskDefinitionId,
      task.taskDefinitionName,
      task.taskDefinitionId ? task.taskId : undefined
    );
    const existingBucket = buckets.get(bucketKey);

    if (existingBucket) {
      existingBucket.tasks.push(task);
    } else {
      buckets.set(bucketKey, {
        tasks: [task],
        sortOrder: fallbackSortBase + buckets.size,
      });
    }
  }

  return buckets;
};

const createBucketRows = (
  buckets: Map<string, ReadingTaskBucket>
): ReadingTaskRow[] => {
  const rows: ReadingTaskRow[] = [];

  const sortedBuckets = [...buckets.entries()].sort(
    ([leftKey, leftBucket], [rightKey, rightBucket]) =>
      leftBucket.sortOrder - rightBucket.sortOrder ||
      leftKey.localeCompare(rightKey)
  );

  for (const [groupKey, bucket] of sortedBuckets) {
    const firstTask = bucket.tasks[0];
    const groupRow: ReadingTaskRow = {
      type: 'group',
      key: groupKey,
      taskDefinition: getTaskSummaryName(bucket.definition, firstTask),
      user: getTaskSummaryUser(bucket.definition, firstTask),
      startDate: getTaskSummaryStartDate(bucket.definition, firstTask),
      endDate: getTaskSummaryEndDate(bucket.definition, firstTask),
      status: getTaskSummaryStatus(bucket.definition, firstTask),
    };
    const detailRows = bucket.tasks.map((task, index) => {
      const tailId =
        task.taskId ?? `${task.readingCode ?? 'rt'}-${index}`;
      return createReadingTaskDetailRow(task, groupKey, tailId);
    });

    rows.push(groupRow, ...detailRows);
  }

  return rows;
};

export const buildReadingTaskRows = (
  definitionsForScope: SupportTaskDefinition[],
  tasksForScope: SupportReadingTask[]
): ReadingTaskRow[] => {
  const buckets = buildReadingTaskBuckets(definitionsForScope, tasksForScope);

  return createBucketRows(buckets);
};

const getVisibleRows = (
  rows: ReadingTaskRow[],
  expanded: Set<string>
): ReadingTaskRow[] => {
  const visibleRows: ReadingTaskRow[] = [];

  for (const row of rows) {
    if (row.type === 'detail' && !expanded.has(row.groupKey)) {
      continue;
    }

    visibleRows.push(row);
  }

  return visibleRows;
};

type GroupActionCellProps = {
  isOpen: boolean;
  rowKey: string;
  onToggle: ToggleGroup;
};

const GroupActionCell: React.FC<GroupActionCellProps> = ({
  isOpen,
  rowKey,
  onToggle,
}) => {
  const handleClick = React.useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      onToggle(rowKey);
    },
    [onToggle, rowKey]
  );

  return (
    <div className='w-full flex items-center justify-end gap-1 pr-1'>
      <button
        type='button'
        onClick={handleClick}
        aria-expanded={isOpen}
        aria-label={isOpen ? 'Collapse' : 'Expand'}
        className='shrink-0 inline-flex items-center justify-center h-3 w-3 rounded-full hover:bg-black/5'
        title={isOpen ? 'Collapse' : 'Expand'}
      >
        <ChevronIcon
          dir={isOpen ? 'up' : 'down'}
          className='h-2 w-2 text-spanishGray transition-transform duration-200'
        />
      </button>
    </div>
  );
};

const patchActionColumn = (
  column: ColumnType<ReadingTaskRow>,
  expanded: Set<string>,
  toggleGroup: ToggleGroup
): ColumnType<ReadingTaskRow> => ({
  ...column,
  render: (value: unknown, row: ReadingTaskRow, index: number) => {
    if (row.type !== 'group') {
      return column.render ? column.render(value, row, index) : null;
    }

    return (
      <GroupActionCell
        isOpen={expanded.has(row.key)}
        rowKey={row.key}
        onToggle={toggleGroup}
      />
    );
  },
});

const patchColumns = (
  columns: ColumnsType<ReadingTaskRow>,
  expanded: Set<string>,
  toggleGroup: ToggleGroup
): ColumnsType<ReadingTaskRow> =>
  columns.map(column =>
    column.key === 'actions'
      ? patchActionColumn(column, expanded, toggleGroup)
      : column
  );

const getRowProps = (
  row: ReadingTaskRow,
  baseCfg: ReadingTasksTableConfig,
  toggleGroup: ToggleGroup
) => {
  const baseRowProps = baseCfg.onRow(row);

  if (row.type !== 'group') {
    return baseRowProps;
  }

  return {
    ...baseRowProps,
    onClick: () => toggleGroup(row.key),
    style: {
      ...(baseRowProps?.style ?? {}),
      cursor: 'pointer',
    },
  };
};

export const ReadingTasks: React.FC<Props> = props => {
  const {
    clinicalTrialId,
    patientId,
    visitId,
    tasks,
    taskDefinitions,
    loading,
    error,
  } = props;

  const invalid = !clinicalTrialId || !patientId;

  const allReadingTasks: SupportReadingTask[] = invalid ? [] : tasks;
  const allTaskDefinitions: SupportTaskDefinition[] = invalid
    ? []
    : taskDefinitions;

  const defsForScope: SupportTaskDefinition[] =
    visitId === undefined
      ? allTaskDefinitions
      : allTaskDefinitions.filter(
          definition => normalizeVisitId(definition.visitId) === normalizeVisitId(visitId)
        );

  const tasksForScope: SupportReadingTask[] =
    visitId === undefined
      ? allReadingTasks
      : allReadingTasks.filter(
          task => normalizeVisitId(task.visitId) === normalizeVisitId(visitId)
        );

  const [expanded, setExpanded] = React.useState<Set<string>>(
    () => new Set<string>()
  );
  const [jsonModalState, setJsonModalState] = React.useState<{
    open: boolean;
    title: string;
    payload?: unknown;
  }>({
    open: false,
    title: '',
  });

  const toggleGroup = React.useCallback((groupKey: string) => {
    setExpanded(previousExpanded => {
      const nextExpanded = new Set(previousExpanded);

      if (nextExpanded.has(groupKey)) {
        nextExpanded.delete(groupKey);
      } else {
        nextExpanded.add(groupKey);
      }

      return nextExpanded;
    });
  }, []);

  const handleJsonModalClose = React.useCallback(() => {
    setJsonModalState({
      open: false,
      title: '',
      payload: undefined,
    });
  }, []);

  const handleViewResult = React.useCallback((row: ReadingTaskDetailRow) => {
    setJsonModalState({
      open: true,
      title: 'Result JSON',
      payload: row.resultData,
    });
  }, []);

  const baseCfg = React.useMemo(
    () =>
      createReadingTasksTableConfig({
        headerBgInline: '#F6F9FF',
        rowBgInline: '#FFFFFF80',
        headerBgClass: 'bg-[#F6F9FF]',
        rowBgClass: 'bg-white',
        onViewResult: handleViewResult,
      }),
    [handleViewResult]
  );

  const columns = React.useMemo(
    () => patchColumns(baseCfg.columns, expanded, toggleGroup),
    [baseCfg.columns, expanded, toggleGroup]
  );

  const rows: ReadingTaskRow[] = React.useMemo(
    () => buildReadingTaskRows(defsForScope, tasksForScope),
    [defsForScope, tasksForScope]
  );

  const visibleRows: ReadingTaskRow[] = React.useMemo(
    () => getVisibleRows(rows, expanded),
    [rows, expanded]
  );

  const handleRow = React.useCallback(
    (row: ReadingTaskRow) => getRowProps(row, baseCfg, toggleGroup),
    [baseCfg, toggleGroup]
  );

  if (invalid) {
    return null;
  }

  if (loading) {
    return <div>Loading reading tasks…</div>;
  }

  if (error) {
    return React.createElement(
      'div',
      { className: 'text-red-600' },
      'Failed to load reading tasks.'
    );
  }

  if (!rows.length) {
    return <div>No reading tasks.</div>;
  }

  return (
    <>
      <TableNew<ReadingTaskRow>
        tableClassName='main-table mt-0'
        rowKey='key'
        columns={columns}
        dataSource={visibleRows}
        pagination={false}
        components={baseCfg.components}
        rowClassName={baseCfg.rowClassName}
        onRow={handleRow}
        style={{ margin: 0 }}
      />
      <JsonPayloadModal
        open={jsonModalState.open}
        onCancel={handleJsonModalClose}
        title={jsonModalState.title}
        payload={jsonModalState.payload}
      />
    </>
  );
};
